import {
  ExternalCourseListSourceType,
  ExternalCourseModernizationStatus,
  type ListExternalCoursesInput,
} from "@axio/graphql/generated/graphql";
import { hasMinRole, UserRoleUI } from "../../../constants/rolesAndPermissions";

export type ModernizerFilter = "all" | "modernized" | "inProgress" | "notStarted";
export type CourseSourceFilter = "all" | ExternalCourseListSourceType;

export const EMPTY_COURSE_SUMMARY = {
  totalCourses: 0,
  modernized: 0,
  inProgress: 0,
  notStarted: 0,
};

export const EVALUATION_NO_RUBRIC_REASON = "Add and activate a ready rubric before evaluating courses.";

const MODERNIZATION_STATUS_BY_FILTER: Record<Exclude<ModernizerFilter, "all">, ExternalCourseModernizationStatus> = {
  modernized: ExternalCourseModernizationStatus.Modernized,
  inProgress: ExternalCourseModernizationStatus.InProgress,
  notStarted: ExternalCourseModernizationStatus.NotStarted,
};

export const IN_PROGRESS_STATUSES: ExternalCourseModernizationStatus[] = [
  ExternalCourseModernizationStatus.Invited,
  ExternalCourseModernizationStatus.IntakeReady,
  ExternalCourseModernizationStatus.IntakeInProgress,
  ExternalCourseModernizationStatus.ReadyToEvaluate,
  ExternalCourseModernizationStatus.Evaluating,
  ExternalCourseModernizationStatus.ReadyForReview,
];

export const MODERNIZED_STATUSES: ExternalCourseModernizationStatus[] = [ExternalCourseModernizationStatus.Published];

export const NOT_STARTED_STATUSES: ExternalCourseModernizationStatus[] = [ExternalCourseModernizationStatus.NotStarted];

export type ModernizerStatusFilterOption = {
  label: string;
  value: ExternalCourseModernizationStatus;
};

export const MODERNIZER_STATUS_FILTER_OPTIONS: ModernizerStatusFilterOption[] = [
  { label: "Not Started", value: ExternalCourseModernizationStatus.NotStarted },
  { label: "Invited", value: ExternalCourseModernizationStatus.Invited },
  { label: "Outcomes Approved", value: ExternalCourseModernizationStatus.IntakeReady },
  { label: "Intake In Progress", value: ExternalCourseModernizationStatus.IntakeInProgress },
  { label: "Ready to Evaluate", value: ExternalCourseModernizationStatus.ReadyToEvaluate },
  { label: "Evaluating", value: ExternalCourseModernizationStatus.Evaluating },
  { label: "Ready for Review", value: ExternalCourseModernizationStatus.ReadyForReview },
  { label: "Modernized", value: ExternalCourseModernizationStatus.Published },
];

export const getStatusFiltersForActiveFilter = (
  activeFilter: ModernizerFilter
): ExternalCourseModernizationStatus[] => {
  switch (activeFilter) {
    case "inProgress":
      return [...IN_PROGRESS_STATUSES];
    case "modernized":
      return [...MODERNIZED_STATUSES];
    case "notStarted":
      return [...NOT_STARTED_STATUSES];
    case "all":
    default:
      return [];
  }
};

export const getActiveFilterForStatusFilters = (
  statusFilters: ExternalCourseModernizationStatus[]
): ModernizerFilter => {
  if (statusFilters.length === 0) {
    return "all";
  }

  const allInProgress = statusFilters.every((status) => IN_PROGRESS_STATUSES.includes(status));
  if (allInProgress) {
    return "inProgress";
  }

  const allModernized = statusFilters.every((status) => MODERNIZED_STATUSES.includes(status));
  if (allModernized) {
    return "modernized";
  }

  const allNotStarted = statusFilters.every((status) => NOT_STARTED_STATUSES.includes(status));
  if (allNotStarted) {
    return "notStarted";
  }

  return "all";
};

export const canManageCourseModernizerRubric = (userRole: UserRoleUI) => hasMinRole(userRole, UserRoleUI.SCHOOL_ADMIN);

export const canViewAllCourseModernizerCourses = (userRole: UserRoleUI) =>
  hasMinRole(userRole, UserRoleUI.SCHOOL_ADMIN);

export const getModernizationStatusForFilter = (
  activeFilter: ModernizerFilter
): ListExternalCoursesInput["modernizationStatus"] =>
  activeFilter === "all" ? undefined : MODERNIZATION_STATUS_BY_FILTER[activeFilter];

export const buildListExternalCoursesInput = ({
  activeFilter,
  facultyFilter,
  isAdmin,
  page,
  pageSize,
  searchQuery,
  sourceFilter,
  statusFilters,
  termFilter,
}: {
  activeFilter: ModernizerFilter;
  facultyFilter: string;
  isAdmin: boolean;
  page: number;
  pageSize: number;
  searchQuery: string;
  sourceFilter: CourseSourceFilter;
  statusFilters: ExternalCourseModernizationStatus[];
  termFilter: string;
}): ListExternalCoursesInput => {
  const sourceType = sourceFilter === "all" ? undefined : sourceFilter;
  const hasStatusFilters = statusFilters.length > 0;

  return {
    page,
    pageSize,
    searchQuery: searchQuery.trim() || undefined,
    facultyId: isAdmin && facultyFilter !== "all" ? facultyFilter : undefined,
    termLabel: termFilter !== "all" ? termFilter : undefined,
    modernizationStatus: hasStatusFilters ? undefined : getModernizationStatusForFilter(activeFilter),
    modernizationStatuses: hasStatusFilters ? statusFilters : undefined,
    sourceType,
  };
};
